import { useEffect, useRef, useState } from "react";
import { Check, Info, Mail, Smartphone, X } from "lucide-react";

import "../assets/css/LoginTwoFactorModal.css";

const emptyCode = () => Array(6).fill("");

const maskEmail = (email = "") => {
  if (!email.includes("@")) return "your registered email";
  const [name, domain] = email.split("@");
  return `${name.slice(0, 1)}${"•".repeat(Math.max(5, name.length - 1))}@${domain}`;
};

const LoginTwoFactorModal = ({
  initialMethod = "authenticator",
  email,
  submitting,
  error,
  onVerify,
  onResend,
  onClose,
  onComplete,
}) => {
  const normalizedMethod = initialMethod === "email" ? "email" : "authenticator";
  const [screen, setScreen] = useState("verify");
  const [method, setMethod] = useState(normalizedMethod);
  const [selectedMethod, setSelectedMethod] = useState(normalizedMethod);
  const [code, setCode] = useState(emptyCode);
  const [resendSeconds, setResendSeconds] = useState(normalizedMethod === "email" ? 45 : 0);
  const inputs = useRef([]);

  useEffect(() => {
    if (screen !== "verify" || !resendSeconds) return undefined;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [screen, resendSeconds]);

  useEffect(() => {
    if (screen === "verify") window.setTimeout(() => inputs.current[0]?.focus(), 0);
  }, [screen, method]);

  const updateDigit = (index, rawValue) => {
    const digit = rawValue.replace(/\D/g, "").slice(-1);
    setCode((current) => current.map((value, position) => (position === index ? digit : value)));
    if (digit && index < 5) inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !code[index] && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === "ArrowLeft" && index > 0) inputs.current[index - 1]?.focus();
    if (event.key === "ArrowRight" && index < 5) inputs.current[index + 1]?.focus();
  };

  const handlePaste = (event) => {
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!digits) return;
    event.preventDefault();
    setCode(Array.from({ length: 6 }, (_, index) => digits[index] || ""));
    inputs.current[Math.min(digits.length, 6) - 1]?.focus();
  };

  const chooseMethod = async () => {
    setMethod(selectedMethod);
    setCode(emptyCode());
    setScreen("verify");
    if (selectedMethod === "email") {
      setResendSeconds(45);
      await onResend(selectedMethod);
    } else {
      setResendSeconds(0);
    }
  };

  const resend = async () => {
    if (resendSeconds || submitting) return;
    const sent = await onResend(method);
    if (sent !== false) {
      setCode(emptyCode());
      setResendSeconds(45);
      inputs.current[0]?.focus();
    }
  };

  const verify = async () => {
    const verified = await onVerify(code.join(""));
    if (verified) setScreen("success");
  };

  return (
    <div className="login-2fa-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="login-2fa-modal" role="dialog" aria-modal="true" aria-labelledby="login-2fa-title">
        <button type="button" className="login-2fa-close" aria-label="Close verification" onClick={onClose} disabled={submitting}>
          <X size={25} />
        </button>

        {screen === "method" && (
          <>
            <h2 id="login-2fa-title" className="login-2fa-method-title">Choose Verification Method</h2>
            <p className="login-2fa-method-intro">How would you like to verify your identity?</p>

            <button type="button" className={`login-2fa-method ${selectedMethod === "authenticator" ? "selected" : ""}`} onClick={() => setSelectedMethod("authenticator")}>
              <Smartphone size={28} />
              <span><strong>Authentication App</strong><small>Use the code from your authentication app.</small></span>
              <i aria-hidden="true" />
            </button>
            <button type="button" className={`login-2fa-method ${selectedMethod === "email" ? "selected" : ""}`} onClick={() => setSelectedMethod("email")}>
              <Mail size={31} />
              <span><strong>Email</strong><small>Send a code to {maskEmail(email)}.</small></span>
              <i aria-hidden="true" />
            </button>

            {error && <p className="login-2fa-method-error" role="alert">{error}</p>}
            <button type="button" className="login-2fa-primary" onClick={chooseMethod} disabled={submitting}>
              {submitting ? "Please wait..." : "Continue"}
            </button>
          </>
        )}

        {screen === "verify" && (
          <>
            <h2 id="login-2fa-title">Verify Your Identity</h2>
            <p className="login-2fa-copy">
              {method === "email" ? <>Enter the 6 digit code sent to<br />{maskEmail(email)}.</> : <>Enter the 6 digit code from your<br />authentication app.</>}
            </p>

            <div className={`login-2fa-code ${error ? "has-error" : ""}`} onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => { inputs.current[index] = element; }}
                  value={digit}
                  placeholder="-"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                  aria-label={`Verification code digit ${index + 1}`}
                  aria-invalid={Boolean(error)}
                  maxLength={1}
                  onChange={(event) => updateDigit(index, event.target.value)}
                  onKeyDown={(event) => handleKeyDown(index, event)}
                />
              ))}
            </div>
            {error && <p className="login-2fa-error" role="alert">{error}</p>}

            {method === "email" && (
              <p className="login-2fa-resend">Didn't receive the code? <button type="button" onClick={resend} disabled={Boolean(resendSeconds) || submitting}>Resend{resendSeconds ? ` (00:${String(resendSeconds).padStart(2, "0")})` : ""}</button></p>
            )}

            <label className="login-2fa-trust">
              <input type="checkbox" />
              <span>Trust this device for 30 days</span>
              <Info size={17} aria-label="You won't be asked for a verification code on this device for 30 days." />
            </label>

            <button type="button" className="login-2fa-primary" disabled={code.join("").length !== 6 || submitting} onClick={verify}>
              {submitting ? "Verifying..." : "Verify & Continue"}
            </button>
            <button type="button" className="login-2fa-link" onClick={() => setScreen("method")} disabled={submitting}>Use a different method</button>
          </>
        )}

        {screen === "success" && (
          <div className="login-2fa-success">
            <div className="login-2fa-success-icon"><Check size={45} strokeWidth={2.3} /></div>
            <h2 id="login-2fa-title">Verification Successful</h2>
            <p>You are now signed in.</p>
            <button type="button" className="login-2fa-success-button" onClick={onComplete}>Continue to your account</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default LoginTwoFactorModal;
